import React from "react";
import MyGig from "Constants/MyGig";

/**
 * Helper for rendering schedule and employee badges
 */
class ScheduleBadgeHelper {
  /**
   * Render substitute badge for employee
   * @param {Object} employee - Employee object
   * @returns {JSX.Element|null} - Substitute badge or null
   */
  static renderSubstituteBadge(employee) {
    const isSubstitute = Number(employee.is_substitute) === 1;
    if (!isSubstitute) return null;
    
    return (
      <span 
        className="ml-1" 
        style={{ 
          backgroundColor: "#b0c4de",
          color: "#333",
          fontSize: "8px", 
          fontWeight: "bold",
          padding: "1px 4px",
          borderRadius: "3px"
        }}
        title="Substitute"
      >
        SUB
      </span>
    );
  }

  /**
   * Render cancel badge for assigned schedule
   * @param {Object} assignedSchedule - Assigned schedule object
   * @returns {JSX.Element|null} - Cancel badge or null
   */
  static renderScheduleCancelBadge(assignedSchedule) {
    const isCancelled =
      Number(assignedSchedule.status) ===
      Number(MyGig.status.ASSIGNED_SCHEDULE_CANCELED);
    if (!isCancelled) return null;

    const isSubstituteSchedule = Number(assignedSchedule.is_substitute) === 1;
    const hasSubstitute = Number(assignedSchedule.had_substitute) === 1;

    let text, backgroundColor, color;
    
    if (isSubstituteSchedule) {
      text = "Cancelled";
      backgroundColor = "#f8b034";
      color = "#333";
    } else if (hasSubstitute) {
      text = "Reassigned";
      backgroundColor = "#86b8a8";
      color = "#333";
    } else {
      text = "Dropped";
      backgroundColor = "rgba(250, 93, 88, 0.8)";
      color = "#333";
    }

    return (
      <span 
        style={{ 
          backgroundColor,
          color,
          fontSize: "8px", 
          fontWeight: "bold",
          padding: "1px 4px",
          borderRadius: "3px",
          marginLeft: "4px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        {text}
      </span>
    );
  }

  /**
   * Get badge configuration for schedule status
   * @param {Object} assignedSchedule - Assigned schedule object
   * @returns {Object|null} - Badge configuration or null
   */
  static getScheduleBadgeConfig(assignedSchedule) {
    const isCancelled =
      Number(assignedSchedule.status) ===
      Number(MyGig.status.ASSIGNED_SCHEDULE_CANCELED);
    if (!isCancelled) return null;

    const isSubstituteSchedule = Number(assignedSchedule.is_substitute) === 1;
    const hasSubstitute = Number(assignedSchedule.had_substitute) === 1;

    if (isSubstituteSchedule) {
      return {
        text: "Cancelled",
        backgroundColor: "#f8b034",
        color: "#333",
        type: "substitute_cancelled"
      };
    } else if (hasSubstitute) {
      return {
        text: "Reassigned", 
        backgroundColor: "#86b8a8",
        color: "#333",
        type: "reassigned"
      };
    } else {
      return {
        text: "Dropped",
        backgroundColor: "rgba(250, 93, 88, 0.8)",
        color: "#333", 
        type: "dropped"
      };
    }
  }

  /**
   * Check if employee is substitute
   * @param {Object} employee - Employee object
   * @returns {boolean} - Is substitute
   */
  static isSubstitute(employee) {
    return Number(employee.is_substitute) === 1;
  }

  /**
   * Check if schedule is cancelled
   * @param {Object} assignedSchedule - Assigned schedule object
   * @returns {boolean} - Is cancelled
   */
  static isScheduleCancelled(assignedSchedule) {
    return Number(assignedSchedule.status) === Number(MyGig.status.ASSIGNED_SCHEDULE_CANCELED);
  }
}

export default ScheduleBadgeHelper;
